import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  addEffect,
  flowMax,
} from 'ad-hok'
import { addEffectOnMount } from 'ad-hok-utils'
import cx from 'classnames'
import { CSSProperties, FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { addNavigate } from 'shared/src/utils/addNavigate'
import { trackEvent } from 'shared/src/utils/analytics'
import { typedAs } from 'shared/src/utils/typedAs'

import backgroundImg from 'assets/background-banner.png'
import closeIcon from 'assets/close-icon.svg'
import { sansSerifFontFamily, serifFontFamily } from '../../utils/styles'
import { NOTIFICATION_Z_INDEX } from '../../utils/zIndex'
import { Button } from '../Button'
import { Banner as BannerInterface } from './index'

const useStyles = createUseStyles({
  backgroundHider: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    bottom: 0,
    filter: 'blur(6px)',
    background: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(1px)',
    zIndex: NOTIFICATION_Z_INDEX,
  },
  container: {
    position: 'fixed',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 320,
    maxWidth: '428px',
    width: '100vw',
    maxHeight: '90vh',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#111111',
    bottom: 0,
    letterSpacing: '0.2px',
    zIndex: NOTIFICATION_Z_INDEX + 1,
  },
  containerBg: {
    minHeight: 260,
    flexGrow: 1,
    width: '100%',
  },
  containerContent: {
    padding: '0 24px',
    width: '90%',
  },
  title: {
    color: '#ffffff',
    fontFamily: serifFontFamily,
    fontSize: 40,
    fontWeight: 700,
    lineHeight: '140%',
    letterSpacing: 0.01,
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    color: '#FFFFFFE0',
    fontFamily: sansSerifFontFamily,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '187%',
    marginBottom: 12,
    textAlign: 'center',
  },
  button: {
    display: 'block',
    textDecoration: 'none',
    width: '100%',
    margin: '13px 0',
    textAlign: 'center',
    padding: '30px 0',
    background:
      'radial-gradient(103.73% 266.85% at 0% 16.08%, #CF8355 0%, #CEAF85 38.44%, #9B876D 100%)',
    borderRadius: 8,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  icon: {
    width: '116px',
    boxShadow: '0px 0px 68px 0px #CBA67A75',
    borderRadius: '30px',
  },
  contentHolder: {
    width: '100%',
    marginTop: '23px',
    display: 'flex',
    alignContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    textDecoration: 'none',
  },
  bottomTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '14px',
    fontFamily: sansSerifFontFamily,
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 1)',
    letterSpacing: 0.16,
    maxWidth: 250,
  },
  textStyle: {
    fontFamily: serifFontFamily,
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: '44.8px',
    letterSpacing: '0.4px',
  },
  caretRight: {
    marginLeft: '8px',
    marginBottom: '1px',
  },
  actionBottom: {
    height: 72,
    marginLeft: '8px',
    marginTop: 21,
    paddingLeft: 21,
    paddingRight: 21,
    backgroundColor: '#ffffff',
    borderRadius: 18,
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    color: '#363432',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 700,
    textDecoration: 'none',
  },
  closeBtn: {
    width: 48,
    height: 48,
    borderRadius: 8,
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(8px)',
    top: '12px',
    right: '12px',
    display: 'flex',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeBtnImg: {
    width: '32px',
  },
  italic: {
    fontStyle: 'italic',
  },
})

interface Props {
  banner: BannerInterface
  onAction: () => void
}

export const Banner: FC<Props> = flowMax(
  addState('touchStart', 'setTouchStart', typedAs<number | null>(null)),
  addState('touchEnd', 'setTouchEnd', typedAs<number | null>(null)),
  addState(
    'dynamicBackgroundImg',
    'setDynamicBackgroundImg',
    typedAs<string | null>(null)
  ),
  addState('bannerApiCompleted', 'setBannerApiCompleted', false),
  addState(
    'bannerContent',
    'setBannerContent',
    typedAs<{ title?: string; message?: string; buttonTitle?: string } | null>(
      null
    )
  ),
  addDisplayName('Banner'),
  addNavigate,
  addApi,
  addEffectOnMount(
    ({
        api,
        setDynamicBackgroundImg,
        setBannerApiCompleted,
        setBannerContent,
      }) =>
      () => {
        api.banners
          .webLock()
          .then((response) => {
            if (response && response.fullImage) {
              setDynamicBackgroundImg(response.fullImage)
            }
            if (response && response.content) {
              setBannerContent(response.content as any)
            }
            setBannerApiCompleted(true)
          })
          .catch((error) => {
            console.error('Failed to fetch web lock banner:', error)
            setBannerApiCompleted(true)
          })
      }
  ),
  addEffect(() => () => {
    document.documentElement.style.setProperty('--allow-pull-refresh', 'none')
  }),
  addHandlers({
    onTouchStart:
      ({ setTouchEnd, setTouchStart }) =>
      (e: any) => {
        setTouchEnd(null)
        setTouchStart(e.targetTouches[0].clientY)
      },
    onTouchMove:
      ({ setTouchEnd }) =>
      (e: any) => {
        setTouchEnd(e.targetTouches[0].clientY)
      },
    onTouchEnd:
      ({ touchStart, touchEnd, onAction }) =>
      () => {
        const minSwipeDistance = 50
        if (!touchStart || !touchEnd) return
        const distance = touchStart - touchEnd
        const isBottomtSwipe = distance < -minSwipeDistance
        if (isBottomtSwipe) {
          document.documentElement.style.setProperty(
            '--allow-pull-refresh',
            'auto'
          )
          onAction()
        }
      },
    onLinkClick:
      ({ banner: { link } }) =>
      () => {
        if (link) {
          if (link.onClickAnalyticsEvent) {
            trackEvent(
              link.onClickAnalyticsEvent.options,
              link.onClickAnalyticsEvent.params
            )
          }
        }
        document.documentElement.style.setProperty(
          '--allow-pull-refresh',
          'auto'
        )
      },
    onClose:
      ({ banner: { unDismissable, onCloseAnalyticsEvent }, onAction }) =>
      () => {
        if (unDismissable) {
          return
        }
        if (onCloseAnalyticsEvent) {
          trackEvent(
            onCloseAnalyticsEvent.options,
            onCloseAnalyticsEvent.params
          )
        }
        document.documentElement.style.setProperty(
          '--allow-pull-refresh',
          'auto'
        )
        onAction()
      },
    preventPropagation: () => (e: any) => {
      e.preventPropagation()
    },
  }),
  addProps(({ banner }) => ({
    classes: useStyles(),
    mainStyle: (() => {
      const style = {} as CSSProperties
      if (banner.fullHeight) {
        style.height = '100%'
        style.maxHeight = '100%'
      }
      console.log('accessibleMenu', banner.accessibleMenu)
      return style
    })(),
  })),
  ({
    banner: { unDismissable, link, accessibleMenu },
    dynamicBackgroundImg,
    bannerApiCompleted,
    bannerContent,
    onTouchStart,
    onTouchEnd,
    onTouchMove,
    onLinkClick,
    onClose,
    preventPropagation,
    classes,
    mainStyle,
  }) => (
    <div>
      <div
        onClick={onClose}
        className={classes.backgroundHider}
        style={accessibleMenu ? { height: 'calc(100% - 75px)' } : undefined}
      ></div>
      <div
        className={cx(classes.container)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={preventPropagation}
        style={mainStyle}
      >
        <div
          className={classes.containerBg}
          style={{
            backgroundImage: bannerApiCompleted
              ? `url(${dynamicBackgroundImg || backgroundImg})`
              : 'none',
          }}
        ></div>
        <div className={classes.containerContent}>
          <h1
            className={classes.title}
            dangerouslySetInnerHTML={{
              __html: bannerContent?.title || 'rod<i>e</i>o ios',
            }}
          />
          <p
            className={classes.subtitle}
            dangerouslySetInnerHTML={{
              __html:
                bannerContent?.message ||
                "<b>Rodeo is even better on the app!</b> <span>Join the community, discover more, and shop in an exciting way, all in one seamless experience. Don't miss out, download now!</span>",
            }}
          />
          {link && (
            <a
              href={link.navigateTo}
              className={classes.button}
              onClick={onLinkClick}
            >
              {bannerContent?.buttonTitle || link.title}
            </a>
          )}
          {!unDismissable && (
            <Button onClick={onClose} className={classes.closeBtn}>
              <img src={closeIcon} alt="" className={classes.closeBtnImg} />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
)
